import { useTheme } from "@deere/fuel-react";
import { Box, Button, Chip } from "@mui/material";
import dayjs, { Dayjs } from "dayjs";
import { useTranslation } from "react-i18next";

export interface ShortcutSectionProps {
  currentSelection: [Dayjs | null, Dayjs | null];
  onClick: (selection: [Dayjs, Dayjs]) => void;
  resetConfig?: {
    days: number;
    months: number;
  };
  shortcutLimit?: number;
}

const SHORTCUTS = new Map<string, [Dayjs, Dayjs]>([
  ["datePicker.currentMonth", [dayjs().startOf("month"), dayjs()]],
  ["datePicker.last3Months", [dayjs().subtract(3, "months"), dayjs()]],
  ["datePicker.last6Months", [dayjs().subtract(6, "months"), dayjs()]],
  ["datePicker.last12Months", [dayjs().subtract(12, "months"), dayjs()]],
]);

const ShortcutSection = ({
  currentSelection: value,
  onClick,
  resetConfig = {
    days: 0,
    months: 3,
  },
  shortcutLimit,
}: ShortcutSectionProps) => {
  const { t } = useTranslation("common");
  const theme = useTheme();

  const shortcutsArray = Array.from(SHORTCUTS.entries());
  const visibleShortcuts = shortcutLimit
    ? shortcutsArray.slice(0, shortcutLimit)
    : shortcutsArray;

  return (
    <Box
      data-testid="shortcut-section"
      sx={{
        padding: "12px 16px 4px 16px",
        display: "flex",
        flexDirection: "column",
        gap: "8px",
      }}
    >
      {visibleShortcuts.map(([shortcutLabel, range]) => {
        const selected =
          range[0].isSame(value[0], "day") && range[1].isSame(value[1], "day");
        const hoveredBackgroundColor =
          theme.name === "jdGreen"
            ? theme.tokens.FuelColorGreen200
            : theme.tokens.FuelColorGray200;
        return (
          <Box key={`shortcut-${shortcutLabel}`}>
            <Chip
              data-testid={`shortcut-${shortcutLabel}`}
              data-selected={selected}
              onClick={() => onClick(range)}
              label={t(shortcutLabel)}
              sx={{
                background: selected
                  ? theme.tokens.FuelColorBrandPrimary
                  : theme.tokens.FuelColorBackgroundActionTertiaryHovered,
                color: selected
                  ? theme.tokens.FuelColorWhite
                  : theme.tokens.FuelColorTextSecondary,
                fontWeight: theme.tokens.FuelFontWeightSemibold,
                "&:hover": {
                  background: selected
                    ? theme.tokens.FuelColorBrandPrimary
                    : hoveredBackgroundColor,
                },
              }}
            />
          </Box>
        );
      })}
      <Button
        data-testid="shortcut-datePicker.reset"
        sx={{
          justifyContent: "left",
          textDecoration: "none",
          width: "fit-content",
        }}
        variant="text"
        onClick={() =>
          onClick([
            dayjs()
              .subtract(resetConfig?.months, "months")
              .subtract(resetConfig?.days, "days"),
            dayjs(),
          ])
        }
      >
        {t("datePicker.reset")}
      </Button>
    </Box>
  );
};

export default ShortcutSection;
